import { useEffect, useState } from "react";
import HeaderBar from "../Shared/HeaderBar/HeaderBar.js"; 
import { Button, CircularProgress, Grid2 } from "@mui/material"; 
import BroadcastersUSMap from "./BroadcastersUSMap.js"; 
import ATSCCountyMap from "./ATSCDMAMap.js";
import { getPlatform, useScreenOrientation } from "../../utilities/orientationAndPlatform.js"; 
import { fetchCityCoordinates, fetchAllChannelData } from "./ChannelData.js";
import { useAuth0 } from "@auth0/auth0-react";
import NavigationBar from "../Shared/NavigationBar/NavigationBar.js";
import StationCards from "./StationCards.js";

export default function MapPage() {
    const orientation = useScreenOrientation();
    const [selectedMap, setSelectedMap] = useState("BroadcastMap"); 
    const [loading, setLoading] = useState(true);
    const [loadingMap, setLoadingMap] = useState(true);
    const [loadingBubbles, setLoadingBubbles] = useState(true);
    const [bubblesData, setBubblesData] = useState([]);
    const { getAccessTokenSilently } = useAuth0();
    const [stateMapData, setUsMapData] = useState(null);
    const [dmaMapData, setDmaMapData] = useState(null);
    const [countyMapData, setCountyMapData] = useState(null);
    const [marketData, setMarketData] = useState(null);

    const MAP_TYPES = {
        BROADCAST: "BroadcastMap",
        ATSC_1_0: "ATSC1.0",
        ATSC_3_0: "ATSC3.0",
    };

    function normalizeString(str) {
        return str
          .replace(/[^a-z0-9]/gi, "") 
          .toLowerCase()
          .trim();
      }

      // Fetch the US map data
    useEffect(() => {
        async function fetchMapData() {
            try {
                const [stateMapResponse, dmaMapResponse, countyMapResponse] = await Promise.all([
                    fetch("/data/us.json"),
                    fetch("/data/DMA.json"),
                    fetch("/data/us-counties2.json")
                ]);
                const [stateMapJson, dmaMapJson, countyMapJson] = await Promise.all([
                    stateMapResponse.json(),
                    dmaMapResponse.json(),
                    countyMapResponse.json()
                ]);
                setUsMapData(stateMapJson);
                setDmaMapData(dmaMapJson);
                setCountyMapData(countyMapJson);
                
            } catch (error) {
                console.error("Error fetching map data:", error);
            } finally {
                setLoadingMap(false);
            }
        }

    fetchMapData();
}, []);

      
      

    useEffect(() => {
        async function fetchData() {
            try {
                const [cityCoordinates, channelData] = await Promise.all([
                    fetchCityCoordinates(),
                    fetchAllChannelData(getAccessTokenSilently),
                ]);

                if (!Array.isArray(cityCoordinates) || cityCoordinates.length === 0) {
                    console.error("City coordinates data is not in the expected format or is empty.");
                    setLoadingBubbles(false);
                    return;
                }

                // Map channel data to city coordinates
                const mappedChannelData = channelData
                    .map(channel => {
                        if (!channel.market) {
                            console.warn(`Channel ${channel.call_sign} has no market data`);
                            return null;
                        }
                        const city = cityCoordinates.find(
                            city => normalizeString(city.name.toLowerCase()) === normalizeString(channel.market.toLowerCase())
                          );
                          
                          
                        if (!city) {
                            console.warn(`Market not found for channel: ${channel.call_sign} (${channel.market})`);
                            console.warn("Looking in cityCoordinates for match to:", normalizeString(channel.market));
                            return null;
                        }
                        if (!Array.isArray(city.coordinates) || city.coordinates.length !== 2) {
                            console.warn(`Invalid coordinates for city: ${city.name}`);
                            return null;
                        }
                        let userCount = Math.floor(Math.random() * 1000) + 157; // Simulate user count
                        return {
                            market: channel.market,
                            coordinates: city.coordinates,
                            dma: city.DMA || "Unknown",
                            station: {
                                call_sign: channel.call_sign,
                                affiliation: channel.affiliation || "No affiliation",
                                value: userCount, //replace this soon with the actual data. channel.devices?
                                channel_id: channel.channel_id,
                                atsc1: channel.atsc1_vp1_deployed,
                                site: channel.website_vod_assets,
                                live: (Math.random() < 0.2),
                            },
                        };
                    })
                    .filter(Boolean); // Remove null entries

                    // Combine stations in the same market
                const finalData = Object.values(
                    mappedChannelData.reduce((marketDataAccumlator, channelData) => {

                        const dmaKey = channelData.dma || "Unknown";
                        if (!marketDataAccumlator[dmaKey]) {
                            marketDataAccumlator[dmaKey] = {
                                market: channelData.dma, 
                                coordinates: channelData.coordinates,
                                stations: [],
                            };
                        }
                        marketDataAccumlator[dmaKey].stations.push(channelData.station);
                        return marketDataAccumlator;
                    }, {})
                ).map(marketData => {
                    //Put live stations on top.
                    marketData.stations.sort((a, b) => (b.live === true) - (a.live === true));
                    const totalValue = marketData.stations.reduce((sum, station) => sum + station.value, 0);
                    return {
                        ...marketData,
                        size: 10, //Math.sqrt(totalValue) / 3, // Bubble size based on total value
                        radioWaveSize: Math.sqrt(totalValue),
                        value: totalValue, // Total value for the market
                    };
                });

                setBubblesData(finalData);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoadingBubbles(false);
            }
        }

        fetchData();
    }, [getAccessTokenSilently]);

    useEffect(() => {
        if (!loadingBubbles && !loadingMap) {
            setLoading(false);
        }
    }, [loadingBubbles, loadingMap]);


    

    // Set top margin based on platform and orientation
    var topPadding = "";
    var mapHeight = "";
    var mapWidth = "";
    const platform = getPlatform();
    if (platform === "mobile") {
        if (orientation === 0) {
            topPadding = "8vh"; 
            mapHeight = "50vh"; 
            mapWidth = "90vw";
        }
        if (orientation === 90) {
                topPadding = "18vh";
                mapHeight = "80vh";
                mapWidth = "70vw";
            }
    }
    if (platform === "desktop") {
            topPadding = "5vh";
            mapHeight = "70vh";
            mapWidth = "60vw";
    }


    // Handle map change and set loading state
    const handleMapChange = (map) => {
        setLoading(true);
        setSelectedMap(map);
        setTimeout(() => setLoading(false), 200);
    };

    return (
        <>
            <HeaderBar />
            <Grid2 
                container 
                direction="column" 
                justifyContent="center" 
                alignItems="center" 
                sx={{ paddingTop: topPadding, paddingBottom: "10vh", gap: 2}}
            >
                <Grid2 container justifyContent="center" spacing={2}>
                    <Grid2>
                        <Button
                            variant={selectedMap === MAP_TYPES.BROADCAST ? "contained" : "outlined"}
                            onClick={() => handleMapChange(MAP_TYPES.BROADCAST)}
                        >
                            Broadcast Map
                        </Button>
                    </Grid2>
                    <Grid2>
                        <Button
                            variant={selectedMap === MAP_TYPES.ATSC_1_0 ? "contained" : "outlined"}
                            onClick={() => handleMapChange(MAP_TYPES.ATSC_1_0)}
                        >
                            ATSC1.0
                        </Button>
                    </Grid2>
                    <Grid2>
                        <Button
                            variant={selectedMap === MAP_TYPES.ATSC_3_0 ? "contained" : "outlined"}
                            onClick={() => handleMapChange(MAP_TYPES.ATSC_3_0)}
                        >
                            ATSC3.0
                        </Button>
                    </Grid2>
                </Grid2>
                <Grid2
                    container
                    spacing={2}
                    sx={{
                        flexGrow: 1,
                        px: 2,
                        py: 2,
                    }}
                    justifyContent="center"
                >
                    <Grid2 
                        xs={12}
                        sm={9}
                        sx={{
                            border: "0.2vh solid white",
                            padding: "1vh",
                            borderRadius: "1vh",
                            backgroundColor: "black",
                            width: mapWidth,
                            height: mapHeight,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        {loading ? (
                            <CircularProgress color="inherit" />
                        ) : (
                            <>
                                {selectedMap === MAP_TYPES.BROADCAST && <BroadcastersUSMap bubblesData={bubblesData} stateMapData={stateMapData} setMarketData={setMarketData} />}
                                {selectedMap === MAP_TYPES.ATSC_1_0 && <ATSCCountyMap dmaData={bubblesData} dmaMapData={dmaMapData} countyMapData={countyMapData} setMarketData={setMarketData}/>}
                                {selectedMap === MAP_TYPES.ATSC_3_0 && <ATSCCountyMap dmaData={bubblesData} dmaMapData={dmaMapData}  countyMapData={countyMapData} setMarketData={setMarketData}/>}
                            </>
                        )}
                    </Grid2>
                    
                    <Grid2
                        xs={12}
                        sm={3}
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "flex-start",
                            mt: { xs: 2, md: 0 },
                            overflow: "hidden"
                        }}>
                        {<StationCards marketData={marketData}/>}
                    </Grid2>
                </Grid2>
            </Grid2>
            <NavigationBar></NavigationBar>
            
        </>
    );
}